import { MessageCodeEnum } from '~/constants/MessageCode';
import { joinName } from '~/utils/stringUtils';
import { OrganizationKbnEnum } from '~/constants/OrganizationKbn';
import { ApiClientError } from '~/error/ApiClientError';
import { ClientError } from '~/error/ClientError';
import { ProxyRepository } from '~/repository/ProxyRepository';
import type { ApiResponse } from '~/types/api/common/apiResponse';
import type { DeliveryCompany } from '~/types/api/master/getDeliveryCompany';
import type { Organization } from '~/types/api/master/getOrganization';
import type { Station } from '~/types/api/master/getStation';
import type { UserCompany } from '~/types/api/master/getUserCompany';
import type { GetChProxyTargetResponse } from '~/types/api/proxy/ch/getChProxyTarget';
import type { StartChProxyResponse } from '~/types/api/proxy/ch/startChProxy';
import type { GetMaxProxyTargetResponse } from '~/types/api/proxy/max/getMaxProxyTarget';
import type { StartMaxProxyResponse } from '~/types/api/proxy/max/startMaxProxy';
import type { ProxyLoginMode } from '~/types/common/proxyLoginMode';
import type { ProxyTargetUser } from '~/types/common/proxyTargetUser';

/**
 * 代理ログイン対象ユーザ情報（表示用）
 */
type ProxyTargetUserInfo = ProxyTargetUser & {
  userName: string;
};

/** 特殊処理を入れる必要のあるエラーメッセージID */
const SPECIAL_ERROR_MESSAGE_CODE = '20018';

/**
 * 代理ログインモーダルのロジック
 *
 * @param displayId 画面ID
 */
export const useProxyLoginModalLogic = (
  displayId: string,
  emits: (_event: 'completed' | 'canceled') => void,
) => {
  const proxyRepository = new ProxyRepository(displayId);
  const { startLoad, endLoad } = useLoadingStore();
  const loginUserStore = useLoginUserStore();
  const { loginUser } = storeToRefs(loginUserStore);
  const logger = useLogger(displayId);
  const {
    getOrganizations,
    getAffiliationStations,
    getUserCompanies,
    getDeliveryCompanies,
  } = useMasterStore();
  // 逆引き（代理ログインユーザの作業駅→組織情報）は作業駅リストを使うため useStation を継続使用する
  const { getStationWithOrganizationByStationCd } = useStation(displayId);
  const { refreshSessionInfo } = useAuth(displayId);
  const { showApiMessage } = useMessage();

  /** モーダルの開閉状態 */
  const isOpen = ref(false);
  /** 代理ログインモード */
  let _mode: ProxyLoginMode | undefined = undefined;

  /** 組織リスト */
  const _organizations = ref<Organization[]>([]);
  /** 駅リスト */
  const _stations = ref<Station[]>([]);
  /** 利用者リスト */
  const _userCompanies = ref<UserCompany[]>([]);
  /** 集配人リスト */
  const _deliveryCompanies = ref<DeliveryCompany[]>([]);
  /** ドライバーリスト */
  const _drivers = ref<ProxyTargetUserInfo[]>([]);

  /** 支社名プルダウンに表示する組織リスト */
  const filteredOrganizations = computed(() => _filterOrganizations());
  /** 駅名プルダウンに表示する駅リスト */
  const filteredStations = computed(() => _filterStations());
  /** 利用者名プルダウンに表示する利用者リスト */
  const filteredUserCompanies = computed(() => _filterUserCompanies());
  /** 集配人名プルダウンに表示する集配人リスト */
  const filteredDeliveryCompanies = computed(() => _filterDeliveryCompanies());
  /** ドライバー名プルダウンに表示するドライバーリスト */
  const filteredDrivers = computed(() => _filterDrivers());

  /** 選択された支社 */
  const selectedOrganization = ref<Organization>();
  /** 選択された駅 */
  const selectedStation = ref<Station>();
  /** 選択された利用者 */
  const selectedUserCompany = ref<UserCompany>();
  /** 選択された集配人 */
  const selectedDeliveryCompany = ref<DeliveryCompany>();
  /** 選択されたドライバー */
  const selectedDriver = ref<ProxyTargetUserInfo>();

  /** 支社名プルダウンが非活性かどうか */
  const isOrganizationDisabled = ref(false);
  /** 駅名プルダウンが非活性かどうか */
  const isStationDisabled = ref(false);
  /** 利用者名プルダウンが非活性かどうか */
  const isUserCompanyDisabled = ref(false);
  /** 集配人名プルダウンが非活性かどうか */
  const isDeliveryCompanyDisabled = ref(false);
  /** ドライバー名プルダウンが非活性かどうか */
  const isDriverDisabled = ref(false);

  /** 集配人名プルダウンのメニューが開いているかどうか（UI状態） */
  const isDeliveryCompanyMenuOpen = ref(false);

  /** 決定ボタンが非活性かどうか */
  const isActionButtonDisabled = computed(() => !selectedDriver.value);

  /**
   * 初期処理を実施した後、モーダルを開く
   *
   * @param mode 代理ログインモード
   */
  const open = async (mode: ProxyLoginMode) => {
    _mode = mode;
    isDeliveryCompanyMenuOpen.value = false;
    await _initialize();
    isOpen.value = true;
  };

  /**
   * 各種選択状態をクリアしてモーダルを閉じる
   */
  const close = () => {
    selectedOrganization.value = undefined;
    isOrganizationDisabled.value = false;
    selectedStation.value = undefined;
    isStationDisabled.value = false;
    selectedUserCompany.value = undefined;
    isUserCompanyDisabled.value = false;
    selectedDeliveryCompany.value = undefined;
    isDeliveryCompanyDisabled.value = false;
    selectedDriver.value = undefined;
    isDriverDisabled.value = false;
    isDeliveryCompanyMenuOpen.value = false;
    isOpen.value = false;
  };

  /**
   * 支社名が変更されたときの処理
   */
  const onOrganizationChanged = () => {
    // 駅名、利用者名、集配人名、ドライバー名をクリアし、活性・非活性状態を更新する
    selectedStation.value = undefined;
    isStationDisabled.value = false;
    selectedUserCompany.value = undefined;
    isUserCompanyDisabled.value = true;
    selectedDeliveryCompany.value = undefined;
    isDeliveryCompanyDisabled.value = true;
    isDeliveryCompanyMenuOpen.value = false;
    selectedDriver.value = undefined;
    isDriverDisabled.value = true;
  };

  /**
   * 駅名が変更されたときの処理
   */
  const onStationChanged = () => {
    // 利用者名、集配人名、ドライバー名をクリアし、活性・非活性状態を更新する
    selectedUserCompany.value = undefined;
    isUserCompanyDisabled.value = false;
    selectedDeliveryCompany.value = undefined;
    isDeliveryCompanyDisabled.value = true;
    isDeliveryCompanyMenuOpen.value = false;
    selectedDriver.value = undefined;
    isDriverDisabled.value = true;
  };

  /**
   * 利用者名が変更されたときの処理
   */
  const onUserCompanyChanged = async () => {
    // 集配人名、ドライバー名をクリアし、活性・非活性状態を更新する
    selectedDeliveryCompany.value = undefined;
    // 集配人扱いの場合のみ選択可能
    isDeliveryCompanyDisabled.value = !selectedUserCompany.value?.isDeliveryPersonHandling;
    isDeliveryCompanyMenuOpen.value = false;
    selectedDriver.value = undefined;
    isDriverDisabled.value = false;

    // ドライバーリストを再検索する
    await _searchDrivers();
  };

  /**
   * 集配人名が変更されたときの処理
   */
  const onDeliveryCompanyChanged = () => {
    // ドライバー名をクリアし、活性・非活性状態を更新する
    selectedDriver.value = undefined;
    isDriverDisabled.value = false;
  };

  /**
   * 集配人を未選択に戻す（placeholder表示）
   *
   * prepend-item 等のUIから呼ぶことを想定し、メニューも閉じる
   */
  const clearDeliveryCompanySelection = () => {
    selectedDeliveryCompany.value = undefined;
    onDeliveryCompanyChanged();
    isDeliveryCompanyMenuOpen.value = false;
  };

  /**
   * 代理ログインを実行する
   */
  const proxyLogin = async () => {
    if (!_validate()) {
      return;
    }

    // 代理ログインAPIを実行する
    // モードによってAPIを呼び分ける
    let response: ApiResponse<StartChProxyResponse> | ApiResponse<StartMaxProxyResponse>;
    startLoad();
    try {
      if (_mode === 'CH') {
        response = await proxyRepository.startChProxy({
          targetPlatId: selectedDriver.value?.platId ?? '',
        });
      }
      else if (_mode === 'MAX') {
        response = await proxyRepository.startMaxProxy({
          targetPlatId: selectedDriver.value?.platId ?? '',
        });
      }
      else {
        logger.error('代理ログインに失敗しました: 不明な代理ログインモードです');
        throw new ClientError(MessageCodeEnum.UNKNOWN_ERROR, displayId);
      }

      if (!response.data) {
        logger.error('代理ログインに失敗しました: レスポンスデータが存在しません');
        return new ClientError(MessageCodeEnum.UNKNOWN_ERROR, displayId);
      }
    }
    catch (error) {
      // 特定エラー（所属情報のミスマッチ）の場合はメッセージ表示後にモーダルを閉じる。それ以外はエラーとする
      logger.error('代理ログインに失敗しました', error as Error);
      if (error instanceof ApiClientError) {
        if (error.status === SPECIAL_ERROR_MESSAGE_CODE) {
          showApiMessage(displayId, error.status, error.message, () => {
            close();
          });
          return;
        }
      }
      throw error;
    }
    finally {
      endLoad();
    }

    // 代理ログインユーザの作業駅に組織情報を付与する
    const stationWithOrganization = await getStationWithOrganizationByStationCd(
      response.data.workStation?.stationCd ?? '',
      response.data.workStation?.stationSubCd ?? '',
    );

    // ログインユーザストアに代理ログインユーザ情報を設定する
    loginUserStore.setProxyLoginUser(
      _mode,
      response.data.platId,
      response.data.accountType,
      response.data.userNameSei,
      response.data.userNameMei,
      response.data.permissions,
      response.data.affiliations,
      stationWithOrganization,
    );

    // モーダルを閉じ、完了イベントを発火する
    _clearInputs();
    close();
    emits('completed');
  };

  /**
   * キャンセルボタン押下時の処理
   */
  const onCanceled = () => {
    _clearInputs();
    close();
    emits('canceled');
  };

  /**
   * 初期処理
   */
  const _initialize = async () => {
    startLoad();
    try {
      // あらかじめセッション情報を更新しておく（代理ログインユーザの所属駅の情報を取得するため）
      await refreshSessionInfo();

      // 各種マスタデータを取得する
      const [organizations, stations, userCompanies, deliveryCompanies] = await Promise.all([
        // 組織リスト取得
        getOrganizations(displayId),
        // 所属駅リスト取得
        getAffiliationStations(displayId),
        // 利用者リスト取得
        getUserCompanies(displayId),
        // 集配人リスト取得
        getDeliveryCompanies(displayId),
      ]);

      _organizations.value = organizations;
      _stations.value = stations;
      _userCompanies.value = userCompanies;
      _deliveryCompanies.value = deliveryCompanies;
    }
    finally {
      endLoad();
    }

    // 選択肢が1件のみのプルダウンを自動選択して非活性にする
    _setDefault();

    // 利用者が選択済みの場合、ドライバーリストを検索する
    if (selectedUserCompany.value) {
      await _searchDrivers();
    }
  };

  /**
   * 組織リストから支社のみを抽出する
   *
   * @returns 支社リスト
   */
  const _filterOrganizations = () => {
    let filteredOrgs: Organization[] = _organizations.value.filter((og) => og.organizationKbn === OrganizationKbnEnum.OFFICE);
    // 所属先が割り当たっている場合はそれに該当する支社のみを取得する
    if (loginUser.value?.affiliations && loginUser.value?.affiliations.length > 0) {
      filteredOrgs = loginUser.value?.affiliations.map((affiliation) => {
        // 駅が割り当たっている場合はそれに該当する支社を取得する
        if (affiliation.stationCd) {
          const organizationCd = _stations.value.find((st) => {
            return st.stationCd === affiliation.stationCd
              && st.stationSubCd === affiliation.stationSubCd;
          })?.organizationCd;

          return _organizations.value.find((og) => {
            return og.organizationKbn === OrganizationKbnEnum.OFFICE
              && og.organizationCd === organizationCd;
          });
        }

        // 支社支店コードが割り当たっている場合（JRF社員想定）はそれに該当する支社を取得する
        if (affiliation.branchCd) {
          // 支社支店コードに紐づく任意の支店を取得し、その支社コードから取得
          const anyBranch = _organizations.value.find((br) => {
            return br.organizationKbn === OrganizationKbnEnum.BRANCH
              && br.organizationCd === affiliation.branchCd;
          });

          if (anyBranch) {
            return _organizations.value.find((og) => {
              return og.organizationKbn === OrganizationKbnEnum.OFFICE
                && og.organizationCd === anyBranch.topOfficeCd;
            });
          }
        }

        // 支社コードが割り当たっている場合（JRF社員想定）はそれに該当する支社を取得する
        if (affiliation.officeCd) {
          return _organizations.value.find((og) => {
            return og.organizationKbn === OrganizationKbnEnum.OFFICE
              && og.organizationCd === affiliation.officeCd;
          });
        }
      })
      // undefinedを除外する
        .filter((org) => {
          return org !== undefined;
        })
      // 重複を除外する
        .filter((org, index, self) => {
          return self.findIndex((o) => o?.organizationKbn === org?.organizationKbn && o?.organizationCd === org?.organizationCd) === index;
        });
      // それ以外は絞り込まない
    }

    return filteredOrgs;
  };

  /**
   * 駅リストから選択された支社に紐づく駅を抽出する
   *
   * @returns フィルタされた駅リスト
   */
  const _filterStations = () => {
    // 支社未選択の場合は空リストを返す
    if (!selectedOrganization.value) {
      return [];
    }

    // ユーザの所属先や組織区分に紐づく駅のみに絞り込む
    let filteredStations: Station[] = _stations.value;
    // 所属先が割り当たっている場合はそれに該当する駅のみを取得する
    if (loginUser.value?.affiliations && loginUser.value?.affiliations.length > 0) {
      filteredStations = loginUser.value?.affiliations.map((affiliation) => {
        // 駅が割り当たっている場合はそれに該当する駅を取得する
        if (affiliation.stationCd) {
          return _stations.value.filter((st) => {
            return st.stationCd === affiliation.stationCd
              && st.stationSubCd === affiliation.stationSubCd;
          });
        }

        // 支社支店コードが割り当たっている場合（JRF社員想定）はそれに該当する駅を取得する
        if (affiliation.branchCd) {
          return _stations.value.filter((st) => {
            return st.branchCd === affiliation.branchCd;
          });
        }

        // 支社コードが割り当たっている場合（JRF社員想定）はそれに該当する駅を取得する
        if (affiliation.officeCd) {
          return _stations.value.filter((st) => {
            return st.organizationCd === affiliation.officeCd;
          });
        }
      })
      // 二次元配列を一次元に変換
        .flatMap((st) => st)
      // undefinedを除外する
        .filter((st) => {
          return st !== undefined;
        })
      // 重複を除外する
        .filter((st, index, self) => {
          return self.findIndex((s) => s?.stationCd === st?.stationCd && s?.stationSubCd === st?.stationSubCd) === index;
        });
    }

    // 選択された支社に紐づく駅を取得する
    return filteredStations.filter((st) => {
      if (selectedOrganization.value?.organizationKbn === OrganizationKbnEnum.OFFICE) {
        return st.organizationCd === selectedOrganization.value?.organizationCd;
      }
      else {
        return st.organizationCd === selectedOrganization.value?.topOfficeCd;
      }
    });
  };

  /**
   * 利用者リストから選択された駅に紐づく利用者を抽出する
   *
   * @returns フィルタされた利用者リスト
   */
  const _filterUserCompanies = () => {
    // 駅名が未選択の場合は空リストを返す
    if (!selectedStation.value) {
      return [];
    }

    // ログインユーザに所属先が割り当たっている場合は、所属先に紐づく利用者のみを抽出する
    let filteredUserCompanies: UserCompany[] = _userCompanies.value;
    if (loginUser.value?.affiliations && loginUser.value?.affiliations.length > 0) {
      filteredUserCompanies = loginUser.value?.affiliations.map((affiliation) => {
        // 利用者が割り当たっている場合はそれに該当する利用者を取得する
        if (affiliation.userCompanyCd) {
          return _userCompanies.value.filter((uc) => {
            return uc.stationCd === affiliation.stationCd
              && uc.stationSubCd === affiliation.stationSubCd
              && uc.userCompanyCd === affiliation.userCompanyCd;
          });
        }

        // 駅が割り当たっている場合はそれに該当する利用者を取得する
        if (affiliation.stationCd) {
          return _userCompanies.value.filter((uc) => {
            return uc.stationCd === affiliation.stationCd
              && uc.stationSubCd === affiliation.stationSubCd;
          });
        }

        // 支社支店コードが割り当たっている場合（JRF社員想定）はそれに該当する利用者を取得する
        if (affiliation.branchCd) {
          return _userCompanies.value.filter((uc) => {
            const belongedStation = _stations.value.find((st) => {
              return st.stationCd === uc.stationCd
                && st.stationSubCd === uc.stationSubCd;
            });
            return belongedStation?.branchCd === affiliation.branchCd;
          });
        }

        // 支社コードが割り当たっている場合（JRF社員想定）はそれに該当する利用者を取得する
        if (affiliation.officeCd) {
          return _userCompanies.value.filter((uc) => {
            const belongedStation = _stations.value.find((st) => {
              return st.stationCd === uc.stationCd
                && st.stationSubCd === uc.stationSubCd;
            });
            return belongedStation?.organizationCd === affiliation.officeCd;
          });
        }
      })
        // 二次元配列を一次元に変換
        .flatMap((uc) => uc)
        // undefinedを除外する
        .filter((uc) => {
          return uc !== undefined;
        })
        // 重複を除外する
        .filter((uc, index, self) => {
          return self.findIndex((u) => {
            return u?.stationCd === uc?.stationCd
              && u?.stationSubCd === uc?.stationSubCd
              && u?.userCompanyCd === uc?.userCompanyCd;
          }) === index;
        });
    }

    return filteredUserCompanies.filter((uc) => {
      return uc.stationCd === selectedStation.value?.stationCd
        && uc.stationSubCd === selectedStation.value?.stationSubCd;
    });
  };

  /**
   * 集配人リストから選択された駅に紐づく集配人を抽出する
   *
   * @returns フィルタされた集配人リスト
   */
  const _filterDeliveryCompanies = () => {
    // 利用者名が未選択の場合は空リストを返す
    if (!selectedUserCompany.value) {
      return [];
    }

    // 選択された利用者が集配人扱いでない場合は空リストを返す
    if (!selectedUserCompany.value.isDeliveryPersonHandling) {
      return [];
    }

    return _deliveryCompanies.value.filter((dc) => {
      return dc.stationCd === selectedUserCompany.value?.stationCd
        && dc.stationSubCd === selectedUserCompany.value?.stationSubCd;
    });
  };

  /**
   * ドライバーリストから選択された利用者と集配人に紐づくドライバーを抽出する
   *
   * @returns フィルタされたドライバーリスト
   */
  const _filterDrivers = () => {
    if (!selectedStation.value) {
      return [];
    }

    return _drivers.value.filter((driver) => {
      return driver.affiliations.some((affiliation) => {
        return affiliation.stationCd === selectedStation.value?.stationCd
          && affiliation.stationSubCd === selectedStation.value?.stationSubCd
          && affiliation.userCompanyCd === selectedUserCompany.value?.userCompanyCd
          && (selectedDeliveryCompany.value
            ? affiliation.deliveryCompanyCd === selectedDeliveryCompany.value?.deliveryCompanyCd
            : true
          );
      });
    });
  };

  /**
   * 選択肢が1件のみのプルダウンを自動選択して非活性にする
   */
  const _setDefault = () => {
    // 支社リスト
    if (filteredOrganizations.value.length === 1) {
      selectedOrganization.value = filteredOrganizations.value[0];
      isOrganizationDisabled.value = true;
    }
    // 駅リスト
    if (filteredStations.value.length === 1) {
      selectedStation.value = filteredStations.value[0];
      isStationDisabled.value = true;
    }
    else if (!selectedOrganization.value) {
      isStationDisabled.value = true;
    }
    // 利用者リスト
    if (filteredUserCompanies.value.length === 1) {
      selectedUserCompany.value = filteredUserCompanies.value[0];
      isUserCompanyDisabled.value = true;
    }
    else if (!selectedStation.value) {
      isUserCompanyDisabled.value = true;
    }
    // 利用者が集配人扱いでない場合、集配人プルダウンを非活性にする
    if (!selectedUserCompany.value || !selectedUserCompany.value.isDeliveryPersonHandling) {
      isDeliveryCompanyDisabled.value = true;
    }
    // 利用者が未選択の場合、ドライバープルダウンを非活性にする
    if (!selectedUserCompany.value) {
      isDriverDisabled.value = true;
    }
  };

  /**
   * 代理ログイン対象のドライバーを検索する
   */
  const _searchDrivers = async () => {
    if (selectedStation.value && selectedUserCompany.value) {
      startLoad();
      try {
        const request = {
          stationCd: selectedStation.value.stationCd,
          stationSubCd: selectedStation.value.stationSubCd,
          userCompanyCd: selectedUserCompany.value.userCompanyCd,
          deliveryCompanyCd: selectedDeliveryCompany.value ? selectedDeliveryCompany.value.deliveryCompanyCd : undefined,
        };

        // モードによってAPIを呼び分ける
        let response: ApiResponse<GetChProxyTargetResponse> | ApiResponse<GetMaxProxyTargetResponse>;
        // CH
        if (_mode === 'CH') {
          response = await proxyRepository.getChProxyTargetUsers(request);
        }
        // MAX
        else if (_mode === 'MAX') {
          response = await proxyRepository.getMaxProxyTargetUsers(request);
        }
        else {
          logger.error('代理ログイン対象ドライバーの検索に失敗しました: 不明な代理ログインモードです');
          throw new ClientError(MessageCodeEnum.UNKNOWN_ERROR, displayId);
        }

        if (response.status === '00000' && response.data?.users) {
          _drivers.value = response.data.users.map((user) => ({
            ...user,
            userName: joinName(user.userNameSei, user.userNameMei),
          }));
        }
      }
      catch (error) {
        logger.error('代理ログイン対象ドライバーの検索に失敗しました', error as Error);
        if (error instanceof ApiClientError) {
          if (error.status === SPECIAL_ERROR_MESSAGE_CODE) {
            showApiMessage(displayId, error.status, error.message, () => {
              close();
            });
            return;
          }
        }
        throw error;
      }
      finally {
        endLoad();
      }
    }
  };

  /**
   * 代理ログインの入力値を検証する
   *
   * @returns 検証結果
   */
  const _validate = () => {
    return !!selectedStation.value && !!selectedUserCompany.value && !!selectedDriver.value;
  };

  /**
   * 入力値をクリアする
   */
  const _clearInputs = () => {
    selectedOrganization.value = undefined;
    isOrganizationDisabled.value = false;
    selectedStation.value = undefined;
    isStationDisabled.value = false;
    selectedUserCompany.value = undefined;
    isUserCompanyDisabled.value = false;
    selectedDeliveryCompany.value = undefined;
    isDeliveryCompanyDisabled.value = false;
    selectedDriver.value = undefined;
    isDriverDisabled.value = false;
    isDeliveryCompanyMenuOpen.value = false;
  };

  return {
    isOpen,
    filteredOrganizations,
    filteredStations,
    filteredUserCompanies,
    filteredDeliveryCompanies,
    filteredDrivers,
    selectedOrganization,
    selectedStation,
    selectedUserCompany,
    selectedDeliveryCompany,
    selectedDriver,
    isOrganizationDisabled,
    isStationDisabled,
    isUserCompanyDisabled,
    isDeliveryCompanyDisabled,
    isDriverDisabled,
    isDeliveryCompanyMenuOpen,
    isActionButtonDisabled,
    open,
    close,
    onOrganizationChanged,
    onStationChanged,
    onUserCompanyChanged,
    onDeliveryCompanyChanged,
    clearDeliveryCompanySelection,
    proxyLogin,
    onCanceled,
  };
};
